import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useOrganizationSettings } from '@/hooks/usePrivacyQueries';
import type { OrganizationSettings } from '@/types/api';

interface OrgSettingsContextValue {
  settings: OrganizationSettings | null;
  isLoading: boolean;
  isError: boolean;
  primaryColor: string;
  secondaryColor: string;
  headerColor: string;
  headerTextColor: string;
  logoPath: string;
  displayName: string;
  headline: string;
  subtitle: string;
  mainSiteUrl: string;
  privacyPolicyUrl: string;
  termsConditionUrl: string;
}

const OrgSettingsContext = createContext<OrgSettingsContextValue | undefined>(undefined);

function computeHeaderTextColor(color: string): string {
  if (!color) return '#000000';

  let r: number, g: number, b: number;

  if (color.match(/^rgb/)) {
    const rgbMatch = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );
    if (rgbMatch) {
      if (rgbMatch[4] === '0') return '#000000';
      r = parseInt(rgbMatch[1]);
      g = parseInt(rgbMatch[2]);
      b = parseInt(rgbMatch[3]);
    } else {
      return '#000000';
    }
  } else {
    const hex = color.replace('#', '');
    const fullHex = hex.length === 3
      ? hex.split('').map(c => c + c).join('')
      : hex;
    const hexValue = parseInt(fullHex, 16);
    r = (hexValue >> 16) & 255;
    g = (hexValue >> 8) & 255;
    b = hexValue & 255;
  }

  // HSP (Highly Sensitive Perceived brightness)
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return hsp >= 127.5 ? '#000000' : '#ffffff';
}

export const OrgSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data: settings, isLoading, isError } = useOrganizationSettings();

  useEffect(() => {
    if (settings) {
      const primary = settings.primaryColor || '#FFB31C';
      const secondary = settings.secondaryColor || '#323130';
      const header = settings.headerColor || '#ffffff';

      document.documentElement.style.setProperty('--color-primary', primary);
      document.documentElement.style.setProperty('--color-secondary', secondary);
      document.documentElement.style.setProperty('--color-header-bg', header);
    }
  }, [settings]);

  const value = useMemo<OrgSettingsContextValue>(() => {
    const primaryColor = settings?.primaryColor || '#FFB31C';
    const secondaryColor = settings?.secondaryColor || '#323130';
    const headerColor = settings?.headerColor || '#ffffff';

    return {
      settings: settings || null,
      isLoading,
      isError,
      primaryColor,
      secondaryColor,
      headerColor,
      headerTextColor: computeHeaderTextColor(headerColor),
      logoPath: settings?.logoPath || '',
      displayName: settings?.displayName || '',
      headline: settings?.privacyCenterHeadline || '',
      subtitle: settings?.privacyCenterSubtitle || '',
      mainSiteUrl: settings?.mainSiteUrl || '',
      privacyPolicyUrl: settings?.privacyPolicyUrl || '',
      termsConditionUrl: settings?.termsConditionUrl || '',
    };
  }, [settings, isLoading, isError]);

  return (
    <OrgSettingsContext.Provider value={value}>
      {children}
    </OrgSettingsContext.Provider>
  );
};

export const useOrgSettings = (): OrgSettingsContextValue => {
  const context = useContext(OrgSettingsContext);
  if (!context) {
    throw new Error('useOrgSettings must be used within OrgSettingsProvider');
  }
  return context;
};
